import { FinanceCalendarEventEntity } from 'src/api/interfaces';
import { getCurrentTime, getMomentTime } from 'src/utilities/DateTimeUtilities';

export const FINANCE_CALENDAR_SCENARIOS = ['MEC', 'Guidance', 'OP', 'Targets'] as const;
export type FinanceCalendarScenario = (typeof FINANCE_CALENDAR_SCENARIOS)[number];

export interface FinanceCalendarDataTypes {
  scenario: string;
  table_item: FinanceCalendarDatum[];
}

export interface FinanceCalendarDatum {
  date: Date;
  event: string;
  description: string;
}

const isValidDateTime = (dt: string | undefined): boolean => {
  if (!dt) return false;
  const d = new Date(dt);
  return !isNaN(d.getTime());
};

export const toFinanceCalendarWidgetData = (events: FinanceCalendarEventEntity[]): FinanceCalendarDataTypes[] => {
  const validEvents = events.filter((e) => isValidDateTime(e.dateTime));
  const dataScenarios = [...new Set(validEvents.map((e) => e.scenario))];
  const allScenarios = [...new Set([...FINANCE_CALENDAR_SCENARIOS, ...dataScenarios])];

  return allScenarios.map((scenario) => {
    const items = validEvents.filter((e) => e.scenario === scenario);
    return {
      scenario,
      table_item: items
        .map((e) => ({
          date: new Date(e.dateTime),
          event: e.event,
          description: e.description
        }))
        .sort((a, b) => a.date.getTime() - b.date.getTime())
    };
  });
};

export const getScenarioWithNearestActivity = (data: FinanceCalendarDataTypes[]): string => {
  const now = getCurrentTime();
  let bestScenario = data[0]?.scenario || '';
  let bestFutureDiff = Infinity;
  let bestPastTodayDiff = -Infinity;
  let hasFutureCandidate = false;

  for (const group of data) {
    // Items are sorted chronologically by toFinanceCalendarWidgetData, so the first future item is the nearest.
    for (const item of group.table_item) {
      const itemTime = getMomentTime(item.date.toISOString());
      if (itemTime.isSameOrAfter(now, 'day')) {
        const diff = itemTime.diff(now, 'minutes');
        if (diff >= 0) {
          // Future or right-now event — prefer the closest upcoming one
          if (diff < bestFutureDiff) {
            bestFutureDiff = diff;
            bestScenario = group.scenario;
            hasFutureCandidate = true;
          }
          break; // Found a future event for this group, stop checking
        } else if (!hasFutureCandidate) {
          // Earlier today (already passed) — only use as fallback if no future event found
          if (diff > bestPastTodayDiff) {
            bestPastTodayDiff = diff;
            bestScenario = group.scenario;
          }
          // Don't break — there may be a future event later in this sorted group
        }
        // else: hasFutureCandidate && diff < 0 → skip this past item, continue to next
      }
    }
  }
  return bestScenario;
};
